import type { ClaimPriorArt } from '../../types'

/**
 * The 0-100 prior-art ruler (figure S1's claim drawer).
 *
 * One scale, drawn once, with the "close" mark on it. The point of the ruler is
 * that a reader can place a claim's own score against the records it was scored
 * against WITHOUT holding two number systems in their head: everything here is
 * the same 0-100 the claim is scored on.
 *
 * The rows are whatever the analysis stored for THIS claim — journals and
 * patents together, closest first. Nothing is computed here; a claim the
 * analysis matched to nothing says so rather than drawing an empty ruler.
 */
export const CLOSE_FROM = 80

export interface PriorArtRulerProps {
  rows: ClaimPriorArt[]
}

export default function PriorArtRuler({ rows }: PriorArtRulerProps) {
  return (
    <div className="mt-1">
      <div className="flex justify-between text-[10.5px] text-gray-500">
        <span>Prior art — similarity on the 0–100 ruler</span>
        <span>close from {CLOSE_FROM}</span>
      </div>
      {rows.length === 0 ? (
        <p className="mt-2 border-t border-sky-100 pt-2 text-[11.5px] text-gray-500">
          No prior-art record in the loaded corpus was matched to this claim.
        </p>
      ) : (
        rows.map((row, i) => (
          <div key={`${row.title}-${row.score}`} className="border-t border-sky-100 py-1.5">
            <div className="flex items-baseline gap-2 text-[12px] text-gray-700">
              <b className={`shrink-0 text-[13px] font-extrabold ${i === 0 ? 'text-iflow-700' : 'text-iflow-900'}`}>
                {row.score}
              </b>
              <span className="flex-1">
                {i === 0 ? `Closest existing record: ${row.score} — ` : ''}
                “{row.title}”
              </span>
              <span className="shrink-0 text-[10.5px] text-gray-500">{row.kind}</span>
            </div>
            <div className="relative mt-1.5 h-1.5 rounded-full bg-slate-200">
              <span
                aria-hidden
                className="absolute -top-0.5 h-2.5 w-px bg-amber-500/70"
                style={{ left: `${CLOSE_FROM}%` }}
              />
              <span
                aria-hidden
                className={`absolute -top-1 h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 border-white ring-1 ring-iflow-300 ${
                  row.score >= CLOSE_FROM ? 'bg-amber-500' : 'bg-iflow-500'
                }`}
                style={{ left: `${Math.max(0, Math.min(100, row.score))}%` }}
              />
            </div>
          </div>
        ))
      )}
    </div>
  )
}
